/**
 * News & newsletter service – frontend API calls.
 */

import api from "./api.service";
import { getEditorialArticle, mergeEditorialArticles } from "../content/news";

const newsListCache = new Map();
const articleCache = new Map();

function getNewsCacheKey({ limit = 20, cursor } = {}) {
  return `${limit}:${cursor || ""}`;
}

function buildNewsQuery({ limit, cursor } = {}) {
  const params = new URLSearchParams();
  if (limit != null) params.set("limit", String(limit));
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export function listNews(params = {}, options = {}) {
  return api.get(`/web/news${buildNewsQuery(params)}`, options);
}

export function getNewsBySlug(slug, options = {}) {
  return api.get(`/web/news/${encodeURIComponent(slug)}`, options);
}

export function listAllNews(options = {}) {
  return api.get("/web/news/admin/all", options);
}

export function createArticle(body, options = {}) {
  return api.post("/web/news", body, options);
}

export function updateArticle(slug, body, options = {}) {
  return api.patch(`/web/news/${encodeURIComponent(slug)}`, body, options);
}

export function deleteArticle(slug, options = {}) {
  return api.delete(`/web/news/${encodeURIComponent(slug)}`, options);
}

// ── News ──────────────────────────────────────────────────────────────────────

/** Fetch published articles. Returns { articles, hasMore, nextCursor } */
export async function getNews({ limit = 20, cursor } = {}) {
  let data;
  try {
    data = await listNews({ limit, cursor });
  } catch (error) {
    if (cursor) throw error;
    data = { articles: [], hasMore: false, nextCursor: null };
  }
  if (!cursor) data = { ...data, articles: mergeEditorialArticles(data.articles) };
  newsListCache.set(getNewsCacheKey({ limit, cursor }), data);
  return data;
}

/** Fetch a single published article by slug. Returns { article } */
export async function getArticle(slug) {
  const localArticle = getEditorialArticle(slug);
  const data = localArticle ? { article: localArticle } : await getNewsBySlug(slug);
  if (data?.article?.slug) {
    articleCache.set(data.article.slug, data);
  } else if (slug) {
    articleCache.set(slug, data);
  }
  return data;
}

export function peekNewsCache({ limit = 20, cursor } = {}) {
  return newsListCache.get(getNewsCacheKey({ limit, cursor })) || null;
}

export function peekArticleCache(slug) {
  if (!slug) return null;
  return articleCache.get(slug)?.article || getEditorialArticle(slug) || null;
}

// ── Newsletter ─────────────────────────────────────────────────────────────────

/** Subscribe an email to the newsletter. Returns { message } */
export async function subscribeNewsletter(email, source = "news-page") {
  const response = await api.post("/web/newsletter/subscribe", { email, source });
  if (!response || typeof response !== "object" || typeof response.message !== "string" || !response.message.trim()) {
    throw new Error("We couldn't confirm your registration. Please try again or contact info@ateno.co.");
  }
  return response;
}

export async function unsubscribeNewsletter(email) {
  return api.delete("/web/newsletter/unsubscribe", {
    headers: {
      "Content-Type": "application/json",
    },
    body: { email },
  });
}
